import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, QrCode, MapPin, Headphones, BookOpen, Compass, Lightbulb } from "lucide-react";
import { IMAGES, SIGNAGE_SERVICES, IDEAL_FOR, BRAND } from "../data/content";
import { FinalCTA } from "./Home";
import { WaveformDecor } from "../components/Waveform";

const ICONS = {
  "Interpretive Planning": Lightbulb,
  "Signage Content Development": BookOpen,
  "Audio Interpretation": Headphones,
  "Cultural Interpretation": MapPin,
  "Visitor Journey Design": Compass,
  "QR Enabled Experiences": QrCode,
};

export default function InterpretiveSignage() {
  return (
    <div data-testid="page-interpretive-signage">
      {/* HERO — featuring the Talking Trails interpretive sign artwork */}
      <section className="relative pt-24 md:pt-28 overflow-hidden bg-brand-ink">
        <div className="relative w-full h-[60vh] md:h-[88vh] min-h-[520px] py-6 md:py-10">
          <img
            src={BRAND.signageHero}
            alt="A Talking Trails interpretive sign installed in the landscape, reading 'Interpretation beyond the sign — transform information into experience'."
            className="absolute inset-0 w-full h-full object-contain"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-brand-ink/70 pointer-events-none" />
          <h1 className="sr-only">Interpretation beyond the sign — Talking Trails interpretive signage and visitor experience.</h1>

          <div className="absolute bottom-6 md:bottom-10 left-0 right-0">
            <div className="container-px flex items-end justify-between gap-4 text-brand-cream">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-ochre animate-pulse-dot" />
                <span className="overline text-brand-cream/90">Interpretive Signage &amp; Visitor Experience</span>
              </div>
              <span className="hidden md:inline text-[11px] uppercase tracking-[0.22em] text-brand-cream/70">
                Scroll to explore ↓
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* BRINGING PLACES TO LIFE */}
      <section className="py-24 md:py-32">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Bringing places to life</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.08] text-brand-charcoal">
              A simple QR code can unlock a much deeper visitor experience.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-brand-muted text-lg leading-relaxed font-light space-y-5">
            <p>
              Interpretive signage has long helped visitors understand landscapes, history
              and culture.
            </p>
            <p>
              Talking Trails extends that experience beyond what can fit on a sign. By
              combining traditional interpretation with audio storytelling, visitors can
              hear local voices, cultural knowledge, oral histories and stories connected
              to the places around them.
            </p>
          </div>
        </div>
      </section>

      {/* MORE THAN INFORMATION — featuring the real app mockup */}
      <section className="bg-brand-sand py-24 md:py-32">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="relative">
              <img
                src={BRAND.appMockup}
                alt="Talking Trails Kuranda Tour Stop on a mobile device"
                className="w-full max-w-md mx-auto drop-shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 hidden md:block bg-brand-cream border border-brand-line/70 px-5 py-3">
                <div className="overline">Scan · Walk · Listen</div>
                <div className="font-serif text-lg text-brand-charcoal mt-1">QR-activated tour stops</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="overline">More than information</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
              The most memorable experiences create connection.
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                "Traditional Owner perspectives",
                "Cultural knowledge",
                "Ranger stories",
                "Oral histories",
                "Ecological interpretation",
                "Heritage storytelling",
                "Community memories",
                "Place-based learning",
              ].map((l) => (
                <li key={l} className="flex items-start gap-3 text-brand-charcoal font-light">
                  <span className="mt-2 inline-block w-1.5 h-1.5 bg-brand-ochre rounded-full shrink-0" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="container-px">
          <div className="overline">Services Include</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-brand-charcoal max-w-3xl">
            End-to-end interpretation, from concept to launch.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-line">
            {SIGNAGE_SERVICES.map((s, i) => {
              const Icon = ICONS[s.title] || Compass;
              return (
                <div
                  key={s.title}
                  data-testid={`signage-service-${i + 1}`}
                  className="bg-brand-cream p-8 md:p-10 group hover:bg-brand-sand transition-colors duration-500"
                >
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 border border-brand-ochre/60 text-brand-ochre flex items-center justify-center">
                      <Icon size={18} />
                    </span>
                    <span className="overline">0{i + 1}</span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-6 text-brand-charcoal">{s.title}</h3>
                  <p className="mt-3 text-brand-muted font-light leading-relaxed text-sm">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="bg-brand-ink text-brand-cream py-24 md:py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${IMAGES.heritageBuilding})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/95 to-brand-ink" />
        <div className="container-px relative z-10">
          <div className="overline text-brand-ochre">Ideal For</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight max-w-3xl">
            Where stories belong — and where visitors come to listen.
          </h2>

          <div className="mt-12 flex flex-wrap gap-3">
            {IDEAL_FOR.map((tag, i) => (
              <span
                key={tag}
                data-testid={`ideal-for-${i + 1}`}
                className="px-5 py-2.5 border border-brand-cream/30 text-brand-cream/85 text-sm hover:border-brand-ochre hover:text-brand-ochre transition-colors"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="py-24 md:py-32">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="overline">Why it works</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
              Visitors increasingly seek experiences <span className="italic font-light">rather than information.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-brand-muted text-lg font-light leading-relaxed">
            <p>
              By combining landscape, story and interpretation, people engage more deeply
              with place.
            </p>
            <p className="mt-4">
              The result is richer visitor experiences, longer engagement times and
              stronger connections to culture, community and Country.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-brand-line pt-8">
              {[
                { k: "3.2×", l: "Longer dwell time" },
                { k: "82%", l: "Recall increase" },
                { k: "100%", l: "Community-led" },
              ].map((m) => (
                <div key={m.l}>
                  <div className="font-serif text-3xl md:text-4xl text-brand-charcoal">{m.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted mt-1">{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
